/** SUNTRONIX ONE — Product Catalogue → Data Quality + Readiness (Phase 1).
 *  Transparent checklist (no opaque AI score) over catalogue_products.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ShieldCheck, Loader2, AlertTriangle, Check, X } from "lucide-react";

export default function CatalogueDataQuality() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/catalogue-v2/data-quality").then(({ data }) => setData(data))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load data quality"));
  }, []);

  if (err) return <div className="p-6 text-[13px] text-rose-600" data-testid="dq-error">{err}</div>;
  if (!data) return <div className="p-6 text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const pct = data.total ? Math.round((data.fully_ready / data.total) * 100) : 0;
  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="catalogue-data-quality-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-slate-900 grid place-items-center text-white"><ShieldCheck className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Catalogue Data Quality</h1>
          <p className="text-[13px] text-slate-500">{data.fully_ready} of {data.total} products are fully catalogue-ready ({pct}%). Transparent checklist — no hidden scoring.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="dq-summary">
        {data.summary.map((s) => (
          <div key={s.key} className="rounded-xl border border-slate-200 bg-white p-3.5" data-testid={`dq-metric-${s.key}`}>
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500 font-medium">
              {s.count > 0 ? <AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> : <Check className="h-3.5 w-3.5 text-emerald-500" />}{s.label}
            </div>
            <div className={"text-[20px] font-bold mt-1 tabular-nums " + (s.count > 0 ? "text-slate-900" : "text-emerald-600")}>{s.count}</div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[13px]" data-testid="dq-table">
          <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2 font-semibold">Product</th>
            <th className="px-3 py-2 font-semibold">Ready</th>
            {data.checks.map((c) => <th key={c.key} className="px-2 py-2 font-semibold text-center">{c.label}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {data.items.map((it) => (
              <tr key={it.code} className="hover:bg-slate-50" data-testid={`dq-row-${it.code}`}>
                <td className="px-3 py-2">{it.name}<span className="block text-[10px] text-slate-400">{it.brand_code} · {it.code}{it.status !== "ACTIVE" ? ` · ${it.status}` : ""}</span></td>
                <td className="px-3 py-2 tabular-nums font-semibold">{it.ready}/{it.total_checks}</td>
                {data.checks.map((c) => (
                  <td key={c.key} className="px-2 py-2 text-center">
                    {it.checks[c.key] ? <Check className="h-4 w-4 text-emerald-500 inline" /> : <X className="h-4 w-4 text-slate-300 inline" />}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
